import React, { useState, useEffect } from 'react';
import ReactGA from 'react-ga4';
import { Button, Checkbox, Form, Loader, Header, Grid, Table } from 'semantic-ui-react';
import Editor from 'react-simple-wysiwyg';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

import { createMarketplaceListing, updateMarketplaceListing, getMarketplaceListingById, getMarketplaceBadges } from '~/api/marketplace';
import type { CreateMarketplaceListingRequest, UpdateMarketplaceListingRequest } from '~/api/marketplace';
import MarketplaceListing from '../MarketplaceListing';
import ListingDealExclusionsTable from '../ListingDealExclusionsTable';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { useProjectContext, usePortfolioContext } from '~/hooks';


import '../styles.scss';

interface MarketplaceControlsProps {
  projectId?: string;
  portfolioId?: string;
}

// interface MarketplaceListing {
//   id: string;
//   projectId?: string;
//   portfolioId?: string | null;
//   description?: string;
//   isVisible?: boolean;
//   appliedBadges?: Array<any>;
//   interestedCompanies?: Array<any>;
//   createdAt?: string;
//   updatedAt?: string;
// }

export const MarketplaceControls: React.FC<MarketplaceControlsProps> = ({ projectId, portfolioId }) => {

  const { permissions } = usePermissions();
  const { project } = useProjectContext();
  const { portfolio } = usePortfolioContext();
  const [listing, setListing] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [badges, setBadges] = useState<any[]>();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    isVisible: false,
    badgeIds: [] as number[],
    description: '',
  });

  if (portfolio) {
    ReactGA.send({
      hitType: 'pageview',
      page: '/portfolio/id/listing',
      title: `${portfolio?.name} | Conductor Solar`
    });
    document.title = `${portfolio?.name} | Conductor Solar`;
  } else {
    ReactGA.send({
      hitType: 'pageview',
      page: '/project/id/listing',
      title: `${project?.name} | Conductor Solar`
    });
    document.title = `${project?.name} | Conductor Solar`;
  }


  useEffect(() => {
    getBadges();
  }, []);

  useEffect(() => {
    if (projectId || portfolioId) {
      checkExistingListing();
    }
  }, [projectId, portfolioId]);

  useEffect(() => {
    if (!initialLoading && listing) {
      handleUpdateListing();
    }
  }, [formData.badgeIds, formData.isVisible]);

  const getBadges = async () => {
    try {
      const fetchedBadges = await getMarketplaceBadges();
      setBadges(fetchedBadges);
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  };

  const checkExistingListing = async () => {
    try {
      const params = {
        projectId: projectId || undefined,
        portfolioId: portfolioId || undefined,
      };
      const existingListing = await getMarketplaceListingById(params);
      // const existingListing = listings.find((l: MarketplaceListing) => l.projectId === projectId);
      if (existingListing) {
        setListing(existingListing);
        const badgeIds =
          existingListing.badges?.map((b: any) => b.id) || [];
        setFormData({
          isVisible: existingListing.isVisible || false,
          badgeIds: badgeIds || [],
          description: existingListing.description || '',
        });
      }
    } catch (error) {
      console.error('Error checking existing listing:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleAddToMarketplace = async () => {
    setLoading(true);
    try {
      const request: CreateMarketplaceListingRequest = {
        projectId,
        portfolioId,
        isVisible: false, // Default to hidden
      };

      const newListing = await createMarketplaceListing(request);
      setListing(newListing);

      // Initialize form with default values
      setFormData({
        isVisible: false,
        badgeIds: [],
        description: '',
      });

    } catch (error) {
      console.error('Error adding to marketplace:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateListing = async () => {
    setLoading(true);
    if (!listing) return;
    try {
      await toast.promise(
        (async () => {
          const request: UpdateMarketplaceListingRequest = {
            projectId,
            isVisible: formData.isVisible,
            badgeIds: formData.badgeIds,
            description: formData.description,
          };
          const updatedListing = await updateMarketplaceListing(listing.id.toString(), request);
          // Update local state with the new listing
          setListing(updatedListing);
          setLoading(false);
        })(),
        {
          loading: 'Updating listing...',
          success: 'Marketplace listing updated',
          error: 'Failed to update marketplace listing',
        }
      );
    } catch (error) {
      console.error('Error updating listing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBadgeChange = (badgeId: number, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      badgeIds: checked
        ? [...prev.badgeIds, badgeId]
        : prev.badgeIds.filter(id => id !== badgeId)
    }));
  };

  const handleSaveDescription = async () => {
    setIsEditing(false);
    handleUpdateListing();
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData(prev => ({
      ...prev,
      description: listing?.description || '',
    }));
  };

  return (

    <div className="marketplace-controls">
      <Header as="h2">Conductor Listing</Header>
      {project?.portfolioId ? (
        <p style={{ color: '#CC0000', fontWeight: 600 }}>ATTENTION: This project is part of a portfolio.</p>
      ) : null}
      <Grid>
        <Grid.Column width={8}>

          {initialLoading ? (
            <div className="loading-container" style={{ marginTop: '40px' }}>
              <Loader active inline="centered" />
              <p>Loading marketplace controls...</p>
            </div>
          ) : (
            <>
              {!listing ? (
                <Button
                  primary
                  onClick={handleAddToMarketplace}
                  loading={loading}
                  disabled={loading}
                >
                  Add to Marketplace
                </Button>
              ) : (
                <>
                  <Form loading={loading}>
                    <Form.Field>
                      <label className="field-label">Visibility</label>
                      <Checkbox
                        toggle
                        label={formData.isVisible ? 'Visible' : 'Hidden'}
                        checked={formData.isVisible}
                        onChange={(e, { checked }) =>
                          setFormData(prev => ({ ...prev, isVisible: checked || false }))
                        }
                      />
                    </Form.Field>

                    <Form.Field>
                      <label className="field-label">Project Badges</label>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '12px 24px',
                        }}
                      >
                        {badges?.map(badge => (
                          <Checkbox
                            key={badge.id}
                            // label={badge.name}
                            label={
                              <label>
                                <div style={{ fontWeight: 600, fontSize: '14px' }}>{badge.name}</div>
                                <div style={{ fontSize: '12px', color: '#888' }}>{badge.description}</div>
                              </label>
                            }
                            checked={formData.badgeIds.includes(badge.id)}
                            onChange={(e, { checked }) =>
                              handleBadgeChange(badge.id, checked || false)
                            }
                          />
                        ))}
                      </div>
                    </Form.Field>

                    {isEditing ? (
                      <Form.Field>
                        <label className="field-label">Description</label>
                        <Editor
                          value={formData.description}
                          onChange={(e) =>
                            setFormData(prev => ({ ...prev, description: e.target.value }))
                          }
                          placeholder="Enter a description for this project listing..."
                        />
                        <div style={{ textAlign: 'right', fontSize: '12px', color: '#999', marginTop: '4px' }}>
                          {formData.description.length}/500
                        </div>
                        <Button
                          primary
                          onClick={handleSaveDescription}
                          loading={loading}
                          disabled={loading}
                          icon="check"
                          className="outlined"
                          content="Save Description"
                        />
                        <Button
                          type="button"
                          primary
                          className="outlined"
                          onClick={handleCancel}
                          icon="x"
                          content="Cancel"
                          style={{ marginLeft: 8 }}
                        />
                      </Form.Field>
                    ) : (
                      <div className="description-preview">
                        <label className="field-label">Description</label>
                        <div className="description-content">
                          <span dangerouslySetInnerHTML={{ __html: formData.description || 'No description provided' }} />
                        </div>
                        <Button type="submit" primary className="outlined" onClick={() => setIsEditing(true)} icon="edit" content="Edit description" />
                      </div>
                    )}
                    <div className="response-date" style={{ marginTop: '28px', marginBottom: '16px', fontSize: '0.9em', color: '#666' }}>
                      Last updated {listing?.updatedAt ? format(new Date(listing.updatedAt), 'MMM d, yyyy h:mm a') : ''}
                    </div>
                  </Form>

                  {listing.interestedCompanies && listing.interestedCompanies.length > 0 && (
                    <div style={{ marginTop: '32px' }}>
                      <Header as="h4">Interested Companies</Header>
                      <Table compact size="small">
                        <Table.Header>
                          <Table.Row>
                            <Table.HeaderCell>Company Name</Table.HeaderCell>
                            <Table.HeaderCell>Last Updated</Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {listing.interestedCompanies
                            .filter((ic: any) => ic.isInterested)
                            .sort((a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
                            .map((ic: any) => (
                              <Table.Row key={ic.company?.id || ic.company?.name}>
                                <Table.Cell>{ic.company?.name || 'Unknown'}</Table.Cell>
                                <Table.Cell>{ic.updatedAt ? format(new Date(ic.updatedAt), 'MMM d, yyyy h:mm a') : ''}</Table.Cell>
                              </Table.Row>
                            ))}
                        </Table.Body>
                      </Table>
                    </div>
                  )}
                </>

              )}
            </>
          )}

        </Grid.Column>
        <Grid.Column width={4}>
          {loading ? (
            <></>
          ) : (
            <>
              {listing ? (
                <MarketplaceListing listing={listing} />
              ) : (
                <></>
              )}
            </>
          )}
        </Grid.Column>
      </Grid>

      <ListingDealExclusionsTable projectId={projectId} portfolioId={portfolioId} />

      {
        !permissions?.isAdmin && (
          <>
            {window.location.assign('/dashboard')}
          </>
        )
      }

    </div >
  );
};

export default MarketplaceControls;